import { useLayoutEffect, useState } from 'react';

/**
 * Pin a bar to the top of the window once its slot scrolls past, and let it
 * go when `endRef` (the content it controls) has scrolled away.
 *
 * Same approach as IndexJumpBar, for the same reason: admin's <main> is
 * `overflow: auto` but never scrolls — the WINDOW does — so `position:
 * sticky` binds to a <main> that never moves and the bar would scroll off.
 * Pinned, the bar is `position: fixed` at the slot's own left edge and width
 * (so it follows the collapsible sidebar); the slot keeps the bar's height in
 * the flow so nothing below jumps.
 *
 * Returns { pin: {left, width} | null, barHeight }.
 */
export default function usePinnedBar(slotRef, barRef, endRef, deps = []) {
  const [pin, setPin] = useState(null);
  const [barHeight, setBarHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const slot = slotRef.current;
      const bar = barRef.current;
      if (!slot || !bar) return;
      const h = bar.offsetHeight;
      setBarHeight((prev) => (prev === h ? prev : h));
      const r = slot.getBoundingClientRect();
      const end = endRef.current;
      const ended = end ? end.getBoundingClientRect().bottom < h : false;
      const next = r.top < 0 && !ended ? { left: r.left, width: r.width } : null;
      setPin((prev) => (
        prev && next && prev.left === next.left && prev.width === next.width ? prev
          : !prev && !next ? prev : next));
    };
    measure();
    // capture: scroll does not bubble; a capturing window listener hears it
    // from whichever element scrolls.
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null;
    if (ro && slotRef.current) ro.observe(slotRef.current);
    return () => {
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { pin, barHeight };
}
